/**
 * The message worth showing a person when an API call fails.
 *
 * DRF answers a rejected write with a field-keyed object -- the reason the
 * server refused is in there, and swallowing it for a flat "that did not work"
 * is how someone ends up staring at a disabled Publish button with no idea
 * which rule they have broken. A 403 is called out separately because it is
 * never something the user can fix by editing the form.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const response = (
    error as {
      response?: { status?: number; data?: unknown };
    }
  )?.response;

  if (!response) return "The server could not be reached.";
  if (response.status === 403) {
    return "Your account does not have permission to do that.";
  }
  if (response.status === 404) return "That no longer exists. Refresh the page.";

  const data = response.data;
  if (typeof data === "string" && data.trim()) return data;

  if (data && typeof data === "object") {
    const parts: string[] = [];
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const text = Array.isArray(value) ? value.join(" ") : String(value);
      if (!text) continue;
      // `detail` and `non_field_errors` are DRF's own keys, and some views
      // answer with `error`; none is a field name, so naming it is noise.
      parts.push(
        key === "detail" || key === "non_field_errors" || key === "error" ? text : `${humanise(key)}: ${text}`,
      );
    }
    if (parts.length) return parts.join(" ");
  }

  return fallback;
}

function humanise(field: string): string {
  const words = field.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A rejected write split for a form: messages for the fields the form shows,
 * placed under those inputs, and one general message for everything else
 * (the server's `detail`, rules spanning fields, or fields the form lacks).
 */
export function apiFieldErrors(
  error: unknown,
  formFields: readonly string[],
  fallback: string,
): { fields: Record<string, string>; message: string } {
  if (!error) return { fields: {}, message: "" };
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  if (!response || response.status === 403 || response.status === 404 || !data || typeof data !== "object") {
    return { fields: {}, message: apiErrorMessage(error, fallback) };
  }
  const fields: Record<string, string> = {};
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (formFields.includes(key)) {
      fields[key] = Array.isArray(value) ? value.join(" ") : String(value);
    } else {
      rest[key] = value;
    }
  }
  const message = Object.keys(rest).length
    ? apiErrorMessage({ response: { status: response.status, data: rest } }, fallback)
    : "";
  return { fields, message: message || (Object.keys(fields).length ? "" : fallback) };
}

/**
 * The message for a failed file download. A download asks for a blob, so the
 * server's JSON reason ("No account is assigned to …") arrives as one too and
 * has to be read out of it before apiErrorMessage can use it.
 */
export async function downloadErrorMessage(error: unknown, fallback: string): Promise<string> {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data;
  if (response && typeof Blob !== "undefined" && data instanceof Blob) {
    try {
      const text = await data.text();
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Not JSON: a short plain-text reason is still worth showing; an HTML error page is not.
        parsed = text.trim().startsWith("<") ? undefined : text;
      }
      return apiErrorMessage({ response: { ...response, data: parsed } }, fallback);
    } catch {
      return fallback;
    }
  }
  return apiErrorMessage(error, fallback);
}
